import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Globe, Menu, X, Briefcase, MapPin, Mail, Phone,
  ArrowRight, Send, FileText, Loader2, Upload, Inbox, ChevronRight
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SOLUSIHIJAU_LOGO = "https://customer-assets.emergentagent.com/job_citrabi-tech/artifacts/kbqc7fcg_logo_solusihijau%20crop.png";

const CareersPage = () => {
  const { language, toggleLanguage } = useLanguage();
  const [careers, setCareers] = useState([]);
  const [companyInfo, setCompanyInfo] = useState({
    company_name: 'Citra Abi Permana',
    logo_url: 'https://customer-assets.emergentagent.com/job_citrabi-tech/artifacts/1bg06gil_image.png',
    address_en: 'Jakarta, Indonesia',
    address_id: 'Jakarta, Indonesia',
    email: 'info@citraabi.com',
    phone: '+62 21 1234 5678',
    career_categories: []
  });
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Detail modal
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedCareer, setSelectedCareer] = useState(null);

  // Apply modal
  const [applyOpen, setApplyOpen] = useState(false);
  const [applyForCareer, setApplyForCareer] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [cvFile, setCvFile] = useState(null);
  const [appForm, setAppForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    cover_letter: ''
  });

  // Math challenge (anti-bot)
  const [mathChallenge, setMathChallenge] = useState({ a: 0, b: 0, answer: '' });

  const generateMathChallenge = () => {
    const a = Math.floor(Math.random() * 10) + 1;
    const b = Math.floor(Math.random() * 10) + 1;
    setMathChallenge({ a, b, answer: '' });
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [careersRes, settingsRes] = await Promise.all([
        axios.get(`${API}/careers?active_only=true`),
        axios.get(`${API}/contact-settings`)
      ]);
      setCareers(careersRes.data || []);
      if (settingsRes.data) setCompanyInfo(settingsRes.data);
    } catch (err) {
      console.error('Failed to load careers:', err);
    } finally {
      setLoading(false);
    }
  };

  const openDetail = (career) => {
    setSelectedCareer(career);
    setDetailOpen(true);
  };

  const openApply = (career) => {
    setApplyForCareer(career);
    setAppForm({ full_name: '', email: '', phone: '', cover_letter: '' });
    setCvFile(null);
    generateMathChallenge();
    setDetailOpen(false);
    setApplyOpen(true);
  };

  const handleCvSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== 'application/pdf') {
      toast.error(language === 'en' ? 'CV must be a PDF file' : 'CV harus berupa file PDF');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error(language === 'en' ? 'File too large. Maximum 10MB' : 'File terlalu besar. Maksimal 10MB');
      return;
    }
    setCvFile(file);
  };

  const handleApplySubmit = async (e) => {
    e.preventDefault();
    if (!applyForCareer) return;

    // Verify math challenge
    const expected = mathChallenge.a + mathChallenge.b;
    const userAnswer = parseInt(mathChallenge.answer, 10);
    if (isNaN(userAnswer) || userAnswer !== expected) {
      toast.error(
        language === 'en'
          ? 'Please solve the math question correctly to verify you are not a robot.'
          : 'Silakan jawab soal matematika dengan benar untuk memverifikasi Anda bukan robot.'
      );
      generateMathChallenge();
      return;
    }

    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('career_id', applyForCareer.id);
      fd.append('full_name', appForm.full_name);
      fd.append('email', appForm.email);
      fd.append('phone', appForm.phone);
      fd.append('cover_letter', appForm.cover_letter);
      if (cvFile) fd.append('cv', cvFile);

      const res = await axios.post(`${API}/careers/apply`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success(
        language === 'en'
          ? `Application submitted! Reference: ${res.data.application_id}`
          : `Lamaran terkirim! Referensi: ${res.data.application_id}`,
        { duration: 6000 }
      );
      setApplyOpen(false);
      setAppForm({ full_name: '', email: '', phone: '', cover_letter: '' });
      setCvFile(null);
    } catch (err) {
      toast.error(
        err.response?.data?.detail ||
        (language === 'en' ? 'Failed to submit application' : 'Gagal mengirim lamaran')
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Group careers by category
  const groupedCareers = careers.reduce((acc, c) => {
    const key = c.category || 'General';
    if (!acc[key]) acc[key] = [];
    acc[key].push(c);
    return acc;
  }, {});

  // Determine display order: prefer settings.career_categories, then any extras
  const orderedCategories = (() => {
    const fromSettings = companyInfo.career_categories || [];
    const present = Object.keys(groupedCareers);
    const ordered = fromSettings.filter(c => present.includes(c));
    const extras = present.filter(c => !fromSettings.includes(c));
    return [...ordered, ...extras];
  })();

  return (
    <div className="min-h-screen bg-white" data-testid="careers-page">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur border-b border-gray-100" data-testid="careers-nav">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-16 lg:h-20">
            <Link to="/" className="flex items-center gap-3" data-testid="careers-logo-link">
              <img src={companyInfo.logo_url} alt={companyInfo.company_name} className="h-10 lg:h-12 w-auto" />
              <span className="hidden sm:block font-heading font-bold text-gray-900 text-sm lg:text-base">
                {companyInfo.company_name}
              </span>
            </Link>

            <div className="hidden lg:flex items-center gap-8">
              <Link to="/" className="text-gray-700 hover:text-emerald-600 font-medium transition-colors">
                {language === 'en' ? 'Home' : 'Beranda'}
              </Link>
              <Link to="/#about" className="text-gray-700 hover:text-emerald-600 font-medium transition-colors">
                {language === 'en' ? 'About Us' : 'Tentang Kami'}
              </Link>
              <Link to="/#services" className="text-gray-700 hover:text-emerald-600 font-medium transition-colors">
                {language === 'en' ? 'Services' : 'Layanan'}
              </Link>
              <Link to="/projects" className="text-gray-700 hover:text-emerald-600 font-medium transition-colors">
                {language === 'en' ? 'Projects' : 'Proyek'}
              </Link>
              <Link to="/careers" className="text-emerald-600 font-semibold transition-colors" data-testid="nav-careers-active">
                {language === 'en' ? 'Career' : 'Karir'}
              </Link>
              <Link to="/#contact" className="text-gray-700 hover:text-emerald-600 font-medium transition-colors">
                {language === 'en' ? 'Contact Us' : 'Hubungi Kami'}
              </Link>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={toggleLanguage}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-gray-100 hover:bg-gray-200 transition-colors text-sm font-medium"
                data-testid="careers-lang-toggle"
              >
                <Globe className="w-4 h-4" />
                {language.toUpperCase()}
              </button>
              <Link to="/admin/login">
                <Button variant="default" className="hidden sm:flex bg-emerald-600 hover:bg-emerald-700 text-white">
                  {language === 'en' ? 'Admin Login' : 'Login Admin'}
                </Button>
              </Link>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2 rounded-md hover:bg-gray-100"
                data-testid="careers-mobile-menu-toggle"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="lg:hidden bg-white border-t border-gray-100">
            <div className="px-6 py-4 space-y-4">
              <Link to="/" className="block text-gray-700 hover:text-emerald-600 font-medium" onClick={() => setMobileMenuOpen(false)}>
                {language === 'en' ? 'Home' : 'Beranda'}
              </Link>
              <Link to="/projects" className="block text-gray-700 hover:text-emerald-600 font-medium" onClick={() => setMobileMenuOpen(false)}>
                {language === 'en' ? 'Projects' : 'Proyek'}
              </Link>
              <Link to="/careers" className="block text-emerald-600 font-semibold" onClick={() => setMobileMenuOpen(false)}>
                {language === 'en' ? 'Career' : 'Karir'}
              </Link>
              <Link to="/#contact" className="block text-gray-700 hover:text-emerald-600 font-medium" onClick={() => setMobileMenuOpen(false)}>
                {language === 'en' ? 'Contact Us' : 'Hubungi Kami'}
              </Link>
            </div>
          </div>
        )}
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-12 bg-gradient-to-br from-emerald-50 via-white to-sky-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium mb-6">
              <Briefcase className="w-4 h-4" />
              {language === 'en' ? 'Join Our Team' : 'Bergabung dengan Tim Kami'}
            </div>
            <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl text-gray-900 leading-tight mb-4" data-testid="careers-hero-headline">
              {language === 'en' ? 'Build the future with us.' : 'Bangun masa depan bersama kami.'}
            </h1>
            <p className="text-xl text-gray-600">
              {language === 'en'
                ? 'Explore opportunities to grow your career in construction and digital innovation.'
                : 'Jelajahi peluang untuk mengembangkan karir Anda di bidang konstruksi dan inovasi digital.'}
            </p>
          </div>
        </div>
      </section>

      {/* Positions */}
      <section className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          {loading ? (
            <div className="flex items-center justify-center py-24" data-testid="careers-loading">
              <Loader2 className="w-8 h-8 text-emerald-600 animate-spin" />
            </div>
          ) : careers.length === 0 ? (
            <div className="text-center py-24" data-testid="careers-empty-state">
              <div className="w-20 h-20 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-6">
                <Inbox className="w-10 h-10 text-gray-400" />
              </div>
              <h3 className="font-heading font-bold text-2xl text-gray-900 mb-3">
                {language === 'en' ? 'Currently no open positions' : 'Saat ini tidak ada posisi yang tersedia'}
              </h3>
              <p className="text-gray-600 max-w-md mx-auto">
                {language === 'en'
                  ? "We don't have any open positions right now. Please check back later or send your CV to our HR team."
                  : 'Saat ini tidak ada posisi yang dibuka. Silakan cek kembali nanti atau kirimkan CV Anda ke tim HR kami.'}
              </p>
              {companyInfo.email && (
                <a href={`mailto:${companyInfo.email}`} className="inline-flex items-center gap-2 mt-6 text-emerald-600 hover:text-emerald-700 font-medium">
                  <Mail className="w-4 h-4" /> {companyInfo.email}
                </a>
              )}
            </div>
          ) : (
            <div className="space-y-12" data-testid="careers-list">
              {orderedCategories.map((category) => (
                <div key={category} data-testid={`category-section-${category}`}>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-1 h-8 bg-emerald-600 rounded-full"></div>
                    <h2 className="font-heading font-bold text-2xl lg:text-3xl text-gray-900">{category}</h2>
                    <span className="text-sm text-gray-500">
                      ({groupedCareers[category].length} {language === 'en' ? 'positions' : 'posisi'})
                    </span>
                  </div>

                  <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                    {/* Table Header */}
                    <div className="hidden md:grid grid-cols-12 gap-4 bg-gray-50 px-6 py-4 border-b border-gray-200 text-sm font-semibold text-gray-700">
                      <div className="col-span-7">{language === 'en' ? 'Position' : 'Posisi'}</div>
                      <div className="col-span-2 text-center">{language === 'en' ? 'Status' : 'Status'}</div>
                      <div className="col-span-3 text-right">{language === 'en' ? 'Action' : 'Aksi'}</div>
                    </div>

                    {/* Rows */}
                    <div className="divide-y divide-gray-100">
                      {groupedCareers[category].map((career) => {
                        const isOpen = career.status === 'open';
                        return (
                          <div
                            key={career.id}
                            className="md:grid md:grid-cols-12 gap-4 px-6 py-5 hover:bg-emerald-50/30 transition-colors"
                            data-testid={`career-row-${career.id}`}
                          >
                            <div className="md:col-span-7 mb-3 md:mb-0">
                              <button
                                onClick={() => openDetail(career)}
                                className="text-left group"
                                data-testid={`career-title-${career.id}`}
                              >
                                <h3 className="font-heading font-semibold text-lg text-gray-900 group-hover:text-emerald-600 transition-colors flex items-center gap-2">
                                  {career.title}
                                  <ChevronRight className="w-4 h-4 opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0 transition-all" />
                                </h3>
                                {career.description && (
                                  <p className="text-sm text-gray-500 mt-1 line-clamp-1">{career.description}</p>
                                )}
                              </button>
                            </div>
                            <div className="md:col-span-2 md:text-center mb-3 md:mb-0">
                              <span
                                className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                                  isOpen
                                    ? 'bg-emerald-100 text-emerald-700'
                                    : 'bg-gray-200 text-gray-600'
                                }`}
                                data-testid={`career-status-${career.id}`}
                              >
                                <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${isOpen ? 'bg-emerald-500' : 'bg-gray-400'}`}></span>
                                {isOpen
                                  ? (language === 'en' ? 'Open' : 'Dibuka')
                                  : (language === 'en' ? 'Closed' : 'Ditutup')}
                              </span>
                            </div>
                            <div className="md:col-span-3 md:text-right">
                              {isOpen ? (
                                <Button
                                  onClick={() => openApply(career)}
                                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                  size="sm"
                                  data-testid={`apply-now-btn-${career.id}`}
                                >
                                  {language === 'en' ? 'Apply Now' : 'Lamar Sekarang'}
                                  <ArrowRight className="ml-1.5 w-3.5 h-3.5" />
                                </Button>
                              ) : (
                                <Button variant="outline" size="sm" disabled>
                                  {language === 'en' ? 'Closed' : 'Ditutup'}
                                </Button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="career-detail-dialog">
          {selectedCareer && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2 mb-2">
                  <span className="px-2.5 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-semibold">
                    {selectedCareer.category}
                  </span>
                  <span
                    className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                      selectedCareer.status === 'open'
                        ? 'bg-emerald-100 text-emerald-700'
                        : 'bg-gray-200 text-gray-600'
                    }`}
                  >
                    {selectedCareer.status === 'open'
                      ? (language === 'en' ? 'Open' : 'Dibuka')
                      : (language === 'en' ? 'Closed' : 'Ditutup')}
                  </span>
                </div>
                <DialogTitle className="text-2xl">{selectedCareer.title}</DialogTitle>
              </DialogHeader>

              <div className="space-y-6 py-2">
                {selectedCareer.description && (
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">
                      {language === 'en' ? 'Description' : 'Deskripsi'}
                    </h4>
                    <p className="text-gray-700 whitespace-pre-wrap">{selectedCareer.description}</p>
                  </div>
                )}

                {selectedCareer.requirements && (
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">
                      {language === 'en' ? 'Requirements' : 'Persyaratan'}
                    </h4>
                    <p className="text-gray-700 whitespace-pre-wrap">{selectedCareer.requirements}</p>
                  </div>
                )}
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setDetailOpen(false)}>
                  {language === 'en' ? 'Close' : 'Tutup'}
                </Button>
                {selectedCareer.status === 'open' && (
                  <Button
                    onClick={() => openApply(selectedCareer)}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="career-detail-apply-btn"
                  >
                    {language === 'en' ? 'Apply Now' : 'Lamar Sekarang'}
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Apply Dialog */}
      <Dialog open={applyOpen} onOpenChange={setApplyOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="apply-dialog">
          <DialogHeader>
            <DialogTitle>
              {language === 'en' ? 'Apply for' : 'Lamar untuk'}: {applyForCareer?.title}
            </DialogTitle>
            <DialogDescription>
              {language === 'en'
                ? 'Fill out the form below. Our HR team will reach out if your profile matches.'
                : 'Isi formulir di bawah ini. Tim HR kami akan menghubungi Anda jika profil Anda cocok.'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleApplySubmit} className="space-y-4 mt-2">
            <div>
              <Label htmlFor="full_name">{language === 'en' ? 'Full Name' : 'Nama Lengkap'} *</Label>
              <Input
                id="full_name"
                required
                value={appForm.full_name}
                onChange={(e) => setAppForm({ ...appForm, full_name: e.target.value })}
                placeholder={language === 'en' ? 'Your full name' : 'Nama lengkap Anda'}
                className="mt-1"
                data-testid="apply-name-input"
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={appForm.email}
                  onChange={(e) => setAppForm({ ...appForm, email: e.target.value })}
                  placeholder="you@email.com"
                  className="mt-1"
                  data-testid="apply-email-input"
                />
              </div>
              <div>
                <Label htmlFor="phone">{language === 'en' ? 'Phone' : 'Telepon'} *</Label>
                <Input
                  id="phone"
                  required
                  value={appForm.phone}
                  onChange={(e) => setAppForm({ ...appForm, phone: e.target.value })}
                  placeholder="+62 812 3456 7890"
                  className="mt-1"
                  data-testid="apply-phone-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="cover_letter">{language === 'en' ? 'Cover Letter / Message' : 'Surat Pengantar / Pesan'} *</Label>
              <Textarea
                id="cover_letter"
                required
                rows={5}
                value={appForm.cover_letter}
                onChange={(e) => setAppForm({ ...appForm, cover_letter: e.target.value })}
                placeholder={language === 'en' ? 'Tell us why you are a great fit for this role...' : 'Ceritakan kenapa Anda cocok untuk posisi ini...'}
                className="mt-1"
                data-testid="apply-cover-letter-input"
              />
            </div>

            <div>
              <Label htmlFor="cv">
                {language === 'en' ? 'CV / Resume (PDF, optional)' : 'CV / Resume (PDF, opsional)'}
              </Label>
              <div className="mt-1 flex items-center gap-3">
                <label
                  htmlFor="cv"
                  className="flex-1 flex items-center gap-2 px-4 py-2.5 border border-dashed border-gray-300 rounded-md cursor-pointer hover:border-emerald-500 hover:bg-emerald-50/50 transition-colors text-sm text-gray-600"
                  data-testid="apply-cv-label"
                >
                  <Upload className="w-4 h-4" />
                  {cvFile
                    ? cvFile.name
                    : (language === 'en' ? 'Click to upload PDF (max 10MB)' : 'Klik untuk unggah PDF (maks 10MB)')}
                </label>
                <input
                  id="cv"
                  type="file"
                  accept="application/pdf"
                  onChange={handleCvSelect}
                  className="hidden"
                  data-testid="apply-cv-input"
                />
                {cvFile && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setCvFile(null)}
                    className="text-red-500 hover:text-red-600"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>

            {/* Math challenge - anti-bot verification */}
            <div className="p-4 bg-emerald-50/60 border border-emerald-200 rounded-lg" data-testid="math-challenge">
              <Label className="flex items-center gap-2 text-emerald-900">
                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-emerald-600 text-white text-xs font-bold">?</span>
                {language === 'en' ? 'Verify you are not a robot' : 'Verifikasi Anda bukan robot'} *
              </Label>
              <div className="flex items-center gap-3 mt-3">
                <div className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-md font-mono text-lg font-semibold text-gray-900 select-none" data-testid="math-question">
                  <span>{mathChallenge.a}</span>
                  <span className="text-emerald-600">+</span>
                  <span>{mathChallenge.b}</span>
                  <span className="text-gray-400">=</span>
                </div>
                <Input
                  type="number"
                  required
                  value={mathChallenge.answer}
                  onChange={(e) => setMathChallenge({ ...mathChallenge, answer: e.target.value })}
                  placeholder="?"
                  className="w-24 font-mono text-lg font-semibold text-center"
                  data-testid="math-answer-input"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={generateMathChallenge}
                  className="text-emerald-700 hover:text-emerald-800 hover:bg-emerald-100"
                  title={language === 'en' ? 'New question' : 'Soal baru'}
                  data-testid="math-refresh-btn"
                >
                  ↻
                </Button>
              </div>
              <p className="text-xs text-emerald-700/80 mt-2">
                {language === 'en'
                  ? 'Please solve this simple math to verify you are human.'
                  : 'Silakan jawab soal matematika sederhana ini untuk memverifikasi Anda adalah manusia.'}
              </p>
            </div>

            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setApplyOpen(false)} disabled={submitting}>
                {language === 'en' ? 'Cancel' : 'Batal'}
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="apply-submit-btn"
              >
                {submitting ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {language === 'en' ? 'Submitting...' : 'Mengirim...'}
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Send className="w-4 h-4" />
                    {language === 'en' ? 'Submit Application' : 'Kirim Lamaran'}
                  </span>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img src={companyInfo.logo_url} alt={companyInfo.company_name} className="h-10 w-auto" />
              <span className="font-heading font-bold">{companyInfo.company_name}</span>
            </div>
            <p className="text-gray-500 text-sm">
              © {new Date().getFullYear()} {companyInfo.company_name}. {language === 'en' ? 'All rights reserved.' : 'Hak cipta dilindungi.'}
            </p>
            <a href="https://solusihijau.com" target="_blank" rel="noopener noreferrer">
              <img src={SOLUSIHIJAU_LOGO} alt="Solusihijau.com" className="h-7 w-auto opacity-70" />
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CareersPage;
